import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router";
import { Button } from "../../components/ui/Button";
import { FormError } from "../../components/ui/Banner";
import { Checkbox, Field, Input } from "../../components/ui/Field";
import { useAction } from "../../hooks/useAction";
import { authApi } from "../../lib/api/auth";
import { PRIVACY_URL, TERMS_URL } from "../../lib/site";
import { useSession } from "../../app/session";
import { AuthLayout } from "./AuthLayout";
import { EmailField, PasswordField } from "./fields";
import { VerifyEmail } from "./VerifyEmail";

/** Create an account, confirm the email with the code the API sends on sign-up, then sign in. */
export function SignUpPage() {
  const { refresh } = useSession();
  const { search } = useLocation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [sent, setSent] = useState(false);

  const signUp = useAction(async () => {
    await authApi.signUp(name.trim(), email, password, { acceptTerms, marketingOptIn });
    setSent(true);
  });

  if (sent) {
    return (
      <AuthLayout title="Confirm your email">
        <VerifyEmail email={email} onVerified={async () => { await authApi.signIn(email, password); await refresh(); }} />
      </AuthLayout>
    );
  }

  const submit = (e: FormEvent) => { e.preventDefault(); void signUp.run(); };
  return (
    <AuthLayout title="Create account" footer={<Link to={`/sign-in${search}`}>already have an account? sign in</Link>}>
      <form onSubmit={submit} className="flex flex-col gap-4.5">
        <Field label="name">
          {({ id }) => <Input id={id} required maxLength={100} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />}
        </Field>
        <EmailField value={email} onChange={setEmail} />
        <PasswordField value={password} onChange={setPassword} isNew />
        <div className="flex flex-col gap-3">
          <Checkbox required checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} label={
            <>I agree to the <a href={TERMS_URL} target="_blank" rel="noopener">Terms of Service</a> and <a href={PRIVACY_URL} target="_blank" rel="noopener">Privacy Policy</a></>
          } />
          <Checkbox checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)} label={
            <>Email me product news and offers <span className="text-muted">(optional)</span></>
          } />
        </div>
        <FormError message={signUp.error} />
        <Button type="submit" variant="primary" pending={signUp.pending} pendingLabel="creating…">create account →</Button>
      </form>
    </AuthLayout>
  );
}
